import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { BackButton } from "@/components/common/BackButton";
import { LoadMore } from "@/components/common/LoadMore";
import { NoBackend } from "@/components/common/NoBackend";
import { OpenListingsDocument, SortOrder } from "@/graphql/generated/operations";
import { usePagedQuery } from "@/lib/use-paged-query";
import { userErrorMessage } from "@/lib/user-error";
import { ListingCard, type ListingTile } from "@/pages/share/listings/ListingCard";
import { ListingReferenceSearch } from "@/pages/share/listings/ListingReferenceSearch";
import { useListingReferenceSearch } from "@/pages/share/listings/listing-reference";

// SHARE · Offene Ausschreibungen. Was genesis der Person zum Bewerben anbietet: wirksam offen und
// von außen lesbar, über eine ihrer Gruppen oder weil öffentlich lesbar
// (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §5)). Die Seite filtert
// nichts selbst; ob eine Bewerbung möglich ist, sagt erst die Detailseite aus `actions.canApply`. Auch
// die Suche nach einer Kennziffer stellt genesis an, über denselben Filter wie in der Verwaltung.
export function OpenListingsPage() {
  const intl = useIntl();
  const search = useListingReferenceSearch();
  const input = useMemo(
    () => ({ order: { publishedOn: SortOrder.Desc }, ...(search.where ? { where: search.where } : {}) }),
    [search.where],
  );
  const paged = usePagedQuery<ListingTile>(OpenListingsDocument, {
    connectionKey: "openListings",
    input,
    pageSize: 24,
    skip: search.impossible,
  });
  // Übersprungen hält Apollo das letzte Ergebnis fest. Zu einer Eingabe, die keine Kennziffer sein kann,
  // gibt es aber keins.
  const { nodes, loading, error, hasMore, loadingMore, loadMoreFailed, loadMore } = search.impossible
    ? { ...paged, nodes: [], error: undefined, hasMore: false }
    : paged;

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="OpenListings">
      <div className="mb-5">
        <BackButton />
      </div>
      <h1 className="smycles-page-title">
        <FormattedMessage id="share.openListings.title" defaultMessage="Offene Ausschreibungen" />
      </h1>
      <p className="mt-4 max-w-md text-smycles-body text-ink">
        <FormattedMessage
          id="share.openListings.subtitle"
          defaultMessage="Stellen, Programme und Aufrufe, auf die du dich gerade bewerben kannst."
        />
      </p>

      <div className="mt-6">
        <ListingReferenceSearch search={search} />
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-smycles-label text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Laden…" />
          </div>
        ) : error ? (
          <p role="alert" className="text-smycles-label text-ink-muted">
            {userErrorMessage(intl, error)}
          </p>
        ) : nodes.length === 0 ? (
          <NoBackend
            label={
              search.active
                ? intl.formatMessage({
                    id: "share.openListings.referenceSearch.empty",
                    defaultMessage: "Keine offene Ausschreibung mit dieser Kennziffer",
                  })
                : intl.formatMessage({
                    id: "share.openListings.empty",
                    defaultMessage: "Gerade ist keine Ausschreibung offen",
                  })
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
              {nodes.map((listing, index) => (
                <ListingCard variant="feed" key={listing.id} listing={listing} index={index} to={`/share/open-listings/${listing.id}`} />
              ))}
            </div>
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        )}
      </div>
    </main>
  );
}
